import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Icon,
  Box,
} from '@chakra-ui/react';
import { FaTrophy, FaBolt, FaBullseye, FaRegLightbulb, FaFire } from 'react-icons/fa';

const GameCompletionModal = ({ 
  isOpen, 
  onClose, 
  stats, 
  onPlayAgain, 
  onShowLeaderboard 
}) => {
  const {
    totalScore,
    roundScores,
    hintsUsed,
    bestStreak,
    maxCombo,
    correctGuesses,
    averageGuesses
  } = stats;

  const perfectRounds = roundScores.filter(round => round.guessCount === 1).length;
  const accuracy = (correctGuesses / roundScores.length) * 100;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader textAlign="center" fontSize="2xl">
          <Icon as={FaTrophy} color="yellow.400" mr={2} />
          Game Complete!
        </ModalHeader>
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Main Stats */}
            <SimpleGrid columns={3} spacing={4}>
              <Stat bg="blue.50" p={4} borderRadius="lg">
                <StatLabel>Total Score</StatLabel>
                <StatNumber color="blue.500">{totalScore}</StatNumber>
                <StatHelpText>Final Score</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg">
                <StatLabel>Best Streak</StatLabel>
                <StatNumber color="green.500">{bestStreak}</StatNumber>
                <StatHelpText>
                  <Icon as={FaFire} color="orange.500" mr={1} />
                  Consecutive Correct
                </StatHelpText>
              </Stat>
              <Stat bg="purple.50" p={4} borderRadius="lg">
                <StatLabel>Perfect Rounds</StatLabel>
                <StatNumber color="purple.500">{perfectRounds}</StatNumber>
                <StatHelpText>First Try Correct</StatHelpText>
              </Stat>
            </SimpleGrid>

            {/* Accuracy Bar */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold">Accuracy</Text>
                <Badge colorScheme="green">{accuracy.toFixed(1)}%</Badge>
              </HStack>
              <Progress 
                value={accuracy} 
                colorScheme="green" 
                height="8px" 
                borderRadius="full" 
              />
            </Box>

            {/* Round Details */}
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="bold" mb={2}>Round Summary</Text>
              {roundScores.map((round, index) => (
                <HStack 
                  key={index}
                  bg={round.score > 0 ? "green.50" : "red.50"}
                  p={3}
                  borderRadius="md"
                  justify="space-between"
                >
                  <Text>Round {round.round}</Text>
                  <Badge 
                    colorScheme={round.difficulty === 'easy' ? 'green' : 
                               round.difficulty === 'medium' ? 'yellow' : 'red'}
                  >
                    {round.difficulty?.toUpperCase()}
                  </Badge>
                  <Text>Score: {round.score}</Text>
                  <Text>Guesses: {round.guessCount}</Text>
                  <Text>Hints: {round.hintsUsed}</Text>
                </HStack>
              ))}
            </VStack>

            {/* Additional Stats */}
            <SimpleGrid columns={2} spacing={4}>
              <HStack bg="gray.50" p={3} borderRadius="md">
                <Icon as={FaBullseye} color="green.500" />
                <Text>Avg. Guesses: {averageGuesses}</Text>
              </HStack>
              <HStack bg="gray.50" p={3} borderRadius="md">
                <Icon as={FaRegLightbulb} color="yellow.500" />
                <Text>Hints Used: {hintsUsed}</Text>
              </HStack>
            </SimpleGrid>

            <Stat>
              <StatLabel>Highest Combo</StatLabel>
              <StatNumber>{maxCombo}x</StatNumber>
              <StatHelpText>Consecutive Correct Answers</StatHelpText>
            </Stat>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={4} width="100%" justify="center">
            <Button 
              colorScheme="blue" 
              onClick={onPlayAgain}
              leftIcon={<Icon as={FaBolt} />}
            >
              Play Again
            </Button>
            <Button 
              variant="outline" 
              onClick={onShowLeaderboard}
              leftIcon={<Icon as={FaTrophy} />}
            >
              View Leaderboard
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GameCompletionModal; 